import { createRoot } from 'react-dom/client'
import React from 'react'
import { StandardError } from '@packages/common/src/errors/standard-error'
import type { Api } from '@packages/surveys-widget-common'
import Root from './Root'
import { postMessageToParent } from './post-message-to-parent'
import { FrameMessageData } from './widget-dom-api'
import { UserRef } from '@packages/surveys-widget-common/src/types'
import { parentWindowWidth } from '@packages/surveys-widget-common/src/OffsetContainer'

const rootRef = React.createRef<Api>()

const userRef: UserRef = {
  // @ts-ignore
  current: null,
}

class SpecificAlreadyInitializedError extends StandardError {}

class SpecificNotInitializedError extends StandardError {}

let frameId = ''

window.addEventListener('message', e => {
  if (e?.data?.type) {
    const data: FrameMessageData = e.data

    switch (data.type) {
      case 'specific.widget.initialize': {
        if (rootRef.current) throw new SpecificAlreadyInitializedError()
        frameId = data.frameId
        parentWindowWidth.current = data.windowWidth

        userRef.current = {
          visitorId: data.visitorId,
          userId: data.userId,
          token: data.token,
        }

        const rootEl = document.createElement('div')
        rootEl.setAttribute('specific-widget', 'true')
        rootEl.className = 'w-full h-full'
        document.body.appendChild(rootEl)

        const root = createRoot(rootEl)
        root.render(
          <Root
            ref={rootRef}
            userRef={userRef}
            appId={data.appId}
            onHeightChange={height => {
              postMessageToParent({
                type: 'specific.widget.set-height',
                height,
                frameId,
              })
            }}
            onDidMount={() => {
              postMessageToParent({
                type: 'specific.widget.mounted',
                frameId,
              })
            }}
            onDidClose={() => {
              postMessageToParent({
                type: 'specific.widget.closed',
                frameId,
              })
            }}
            onConfig={opts => {
              postMessageToParent({
                type: 'specific.widget.config',
                frameId,
                ...opts,
              })
            }}
            onFirstClick={() => {
              postMessageToParent({
                type: 'specific.widget.first-touch',
                frameId,
              })
            }}
            onInputFocus={() => {
              postMessageToParent({
                type: 'specific.widget.input-focus',
                frameId,
              })
            }}
            onInputBlur={() => {
              postMessageToParent({
                type: 'specific.widget.input-blur',
                frameId,
              })
            }}
          />
        )

        break
      }
      case 'specific.widget.open': {
        if (!rootRef.current) throw new SpecificNotInitializedError()

        rootRef.current.open(data.surveyId, {
          showInitialLoading: data.showInitialLoading,
          executed: data.executedFrom,
          autoFocus: data.autoFocus,
        })
        break
      }

      case 'specific.widget.user': {
        userRef.current = {
          visitorId: data.visitorId,
          userId: data.userId,
          token: data.token,
        }

        break
      }

      case 'specific.widget.window-width': {
        parentWindowWidth.current = data.width
        break
      }
    }
  }
})
